import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE = "eafg_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE) || "[]");
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE, JSON.stringify(items));
  }, [items]);

  const add = useCallback((product, quantity = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((x) => x.product_id === product.id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + quantity };
        return copy;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          slug: product.slug,
          name_es: product.name_es,
          name_en: product.name_en,
          price: product.price,
          image: (product.images || [])[0] || null,
          quantity,
        },
      ];
    });
  }, []);

  const remove = useCallback((product_id) => {
    setItems((prev) => prev.filter((x) => x.product_id !== product_id));
  }, []);

  const update = useCallback((product_id, quantity) => {
    setItems((prev) =>
      prev
        .map((x) => (x.product_id === product_id ? { ...x, quantity: Math.max(1, quantity) } : x))
        .filter((x) => x.quantity > 0)
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const total = useMemo(
    () => items.reduce((s, it) => s + it.price * it.quantity, 0),
    [items]
  );
  const count = useMemo(() => items.reduce((s, it) => s + it.quantity, 0), [items]);

  return (
    <CartContext.Provider value={{ items, add, remove, update, clear, total, count }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
